import React, { useContext, useState } from 'react';
import { Link } from 'gatsby';
import { navigate } from 'gatsby-link';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleDown } from '@fortawesome/pro-light-svg-icons';

import { GlobalContext } from '../state/GlobalContext';
import { getAccreditationTypes, getBalanceGases, getCylinderSizes, getCylinderSizeFormatted } from '../helpers/helper';
import mixtureGasData from '../data/mixture-gases.json';

import Advert from '../components/sections/Advert';
import Banner from '../components/sections/Banner';
import ComponentFieldGroup from '../components/form/ComponentFieldGroup';
import StepProgress from '../components/form/StepProgress';
import Wrapper from '../components/structure/Wrapper';
import Seo from '../components/structure/Seo';

const FieldWrapper = styled.div`
    --secondary: #00a8e1;

    width: 100%;
    margin-bottom: 1.5rem;

    @media screen and (min-width: 768px) {
        display: flex;
        align-items: flex-start;
    }

    label,
    > .label {
        display: block;
        width: 100%;
        margin-bottom: 1rem;
        padding-left: 1.25rem;

        @media screen and (min-width: 768px) {
            width: 200px;
            flex-basis: 200px;
        }
    }

    .custom-select {
        position: relative;

        select {
            width: 100%;
            padding-right: 30px;
            background: #fff;
        }
    }

    .custom-select-toggle {
        position: absolute;
        right: 6px;
        top: 50%;
        line-height: 1;
        transform: translateY(-50%);
        pointer-events: none;
    }

    select,
    input[type="text"],
    input[type="number"],
    textarea {
        padding: 0.675rem 1.25rem;
        font-weight: 300;
        border: 1px solid var(--secondary);
        border-radius: 20px;
        box-shadow: -6px -6px 10px #E9E9E9;
    }

    input[type="number"] {
        -moz-appearance: textfield;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    }

    select {
        line-height: 1.2;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        -ms-appearance: none
    }

    input {
        &[type="text"], 
        &[type="number"] {
            line-height: 1.2;
        }

        &[name*="target"] {
            padding-left: .75rem;
            padding-right: .75rem;
        }
    }

    textarea {
        width: 100%;
        
        @media screen and (min-width: 768px) {
            width: auto;
            flex-grow: 1;
        }
    }

    .select-search {
        position: relative;
        padding: 0.675rem 1.25rem;
        font-weight: 300;
        border: 1px solid var(--secondary);
        border-radius: 20px;
        box-shadow: -6px -6px 10px #E9E9E9;

        .select-search__input {
            width: 100%;
        }
    }

    .select-search__select {
        background: #fff;
        position: absolute;
        left: 0;
        right: 0;
        z-index: 100;
    }

    .select-search__value {
        line-height: 1;
    }

    /**
    * Options
    */
    .select-search__options {
        list-style: none;
    }

    /**
    * Option row
    */
    .select-search__row:not(:first-child) {
        border-top: 1px solid #eee;
    }

    /**
    * Option
    */
    .select-search__option,
    .select-search__not-found {
        display: block;
        height: auto;
        width: 100%;
        padding: 4px;
        background: #fff;
        border: none;
        outline: none;
        font-family: 'Noto Sans', sans-serif;
        font-size: 14px;
        text-align: left;
        cursor: pointer;

        &:hover {
            color: var(--secondary)
        }
    }

    select, input, textarea {
        &.error {
            border: 1px solid red;
        }
    }

    span.error {
        display: block;
        height: 0;
        padding-left: 1.25rem;
        font-size: .875rem;
        color: red;

        &[aria-hidden="false"] {
            height: auto;
        }
    }

    .global-error {
        display: block;
        height: 0;
        color: red;
        font-size: 0.875rem;

        &[aria-hidden="false"] {
            height: auto;
        }
    }

`;

export default function CustomMixture() {
    const accredTypes = getAccreditationTypes(mixtureGasData);
    const balanceGases = getBalanceGases(mixtureGasData);
    const cylinderSizes = getCylinderSizes(mixtureGasData);

    const { addCustomItem } = useContext(GlobalContext);

    const [compArr, setCompArr] = useState([new Date().getTime()]);

    // Store each component ID in an array
    const addComponentGroup = () => {
        if(compArr.length) {
            setCompArr([...compArr, new Date().getTime()])
        }
    }

    // Remove component from state using given index
    const removeGasComponent = (e, index) => {
        let prevCompArr = [...compArr];
        prevCompArr.splice(index, 1);
        setCompArr([...prevCompArr])
    }

    const validateField = (e) => {
        const field = e.target;
        let errorElem = field.parentElement.nextSibling;
        
        switch(true) {
            case /base/.test(field.name):
                if( field.validity.valueMissing ) {
                    errorElem.textContent = 'Please choose a base gas'
                    addError(errorElem, field);
                } else {
                    errorElem.textContent = '';
                    removeError(errorElem, field);
                }
                break;
            case /size/.test(field.name):
                if( field.validity.valueMissing ) {
                    errorElem.textContent = 'Please choose a cylinder size'
                    addError(errorElem, field);
                } else {
                    errorElem.textContent = '';
                    removeError(errorElem, field);
                }
                break;
            case /target/.test(field.name):
                // TODO: should all target fields be checked to ensure total value is not greater than 100?
                errorElem = field.closest('.component-group-outer').querySelector('.error-target');
                if( field.validity.patternMismatch ) {
                    errorElem.textContent = 'Please enter only numbers'
                    addError(errorElem, field);
                } else {
                    errorElem.textContent = '';
                    removeError(errorElem, field);
                }
                break;
            case /Type/.test(field.name):
                if( field.validity.valueMissing ) {
                    errorElem.textContent = 'Please choose an accreditation';
                    addError(errorElem, field);
                } else {
                    errorElem.textContent = '';
                    removeError(errorElem, field)
                }
                break;
            case /unit/.test(field.name):
                errorElem = field.closest('.component-group-outer').querySelector('.error-unit');
                if( field.validity.valueMissing ) {
                    errorElem.textContent = 'Please choose a unit';
                    addError(errorElem, field);
                } else {
                    errorElem.textContent = '';
                    removeError(errorElem, field)
                }
                break;
            default:
                break;

        }

        function addError(errorElem, field) {
            field.classList.add('error');
            errorElem.setAttribute('aria-hidden', 'false')
        }

        function removeError(errorElem, field) {
            field.classList.remove('error');
            errorElem.setAttribute('aria-hidden', 'true')
        }
    }

    const submitForm = (e) => {
        e.preventDefault();
        
        const form = document.querySelector("form");
        const formData = new FormData(form);
        const componentInputs = document.getElementsByClassName('select-search__input');
        const componentGroups = document.getElementsByClassName('component-group');
        const globalErrorElem = document.getElementsByClassName('global-error')[0];

        // Check if form is valid before proceeding
        if( !form.checkValidity() ) {
            globalErrorElem.textContent = 'Complete all fields with valid data before proceeding.';
            globalErrorElem.setAttribute('aria-hidden', 'false')
            return;
        } else {
            globalErrorElem.textContent = '';
            globalErrorElem.setAttribute('aria-hidden', 'true')
        }
        
        // Add the component input field data to the formData object
        // Without this the data is not picked up by the FormData API
        for(let i=0; i< componentInputs.length;i++) {
            formData.append(`component-${i+1}`, componentInputs[i].value)
        };

        // Build custom mixture object using same struture as mixture-gases.json
        let customMixtureObj = { 
            PR: 'custom',
            base: formData.get('base'),
            size: formData.get('size'),
            Type: formData.get('Type'),
            components: [],
            application: formData.get('application')
        };

        // Add the chosen component data
        for( let j=0; j < componentGroups.length; j++) {
            let componentObj = {
                component: componentGroups[j].querySelector('.select-search__input').value,
                concentration: componentGroups[j].querySelector(`input[name="target-${j}"]`).value,
                unit: componentGroups[j].querySelector(`select[name="unit-${j}"]`).value
            }
            customMixtureObj.components.push(componentObj);
        }

        // Store customMixtureObj in state (quote)
        addCustomItem(customMixtureObj);
        
        // Send user to the /find page to view custom product and close matches
        navigate(
            "/find",
            {
                state: {
                    prev: "/custom-mixture"
                }
            }
        )
    }

    const componentGroupList = compArr.map((item, index) => {
        return <ComponentFieldGroup key={item} id={item} index={index} removeGasComponent={removeGasComponent} validateField={validateField} />
    });

    return (
        <main className="overflow-hidden">
            <Seo 
                title="Custom Mixture"
                description="Create your own gas mixture. Perfect for use cases not covered by our off the shelf mixures." />

            <Banner 
                title="Welcome to the Air Products Mixture Selector"
                subtitle="Browse ready-made gas mixtures by application or create your own"
                showQuote="true" />

            <StepProgress step={parseInt(2)} />

            <Wrapper>
                <form className="custom-mixture-form max-w-2xl mx-auto">

                    <FieldWrapper>
                        <label htmlFor="type">Required accreditation<sup>*</sup></label>
                        <div>
                            <div className="custom-select md:flex-grow">
                                <select name="Type" id="type" className="w-full" required onChange={validateField}>
                                    <option value="">Select accreditation</option>
                                    {
                                        accredTypes.map((item, index) => {
                                            return <option key={`accred-${index}`}value={item}>{item}</option>
                                        })
                                    }
                                </select>
                                <span className="custom-select-toggle text-secondary text-2xl">
                                    <FontAwesomeIcon icon={faChevronCircleDown} />
                                </span>
                            </div>
                            <span className="error"></span>
                        </div>
                    </FieldWrapper>     
                    
                    <FieldWrapper>
                        <span className="label">Components<sup>*</sup></span>
                        <div>
                            <div className="component-field-container">
                                {componentGroupList}
                            </div>
                            <button 
                                type="button"
                                onClick={addComponentGroup}
                                className="inline-block py-4 px-8 leading-tight uppercase text-secondary font-bold rounded-full border border-secondary shadow-lg tracking-widest hover:bg-secondary hover:text-white transition-colors">Add Component</button>
                                
                        </div>
                    </FieldWrapper>

                    <FieldWrapper>
                        <label htmlFor="base">Balance gas<sup>*</sup></label>
                        <div className="flex-grow">
                            <div className="custom-select md:w-1/2">
                                <select name="base" id="base" required onChange={validateField}>
                                    <option value="">Select Balance gas</option>
                                    {
                                        balanceGases.map((item, index) => {
                                            return <option key={`base-${index}`} value={item}>{item}</option>
                                        })
                                    }
                                </select>
                                <span className="custom-select-toggle text-secondary text-2xl">
                                    <FontAwesomeIcon icon={faChevronCircleDown} />
                                </span>
                            </div>
                            <span className="error"></span>
                        </div>
                    </FieldWrapper>

                    <FieldWrapper>
                        <label htmlFor="size">Cylinder size<sup>*</sup></label>
                        <div className="flex-grow">
                            <div className="custom-select md:w-1/2">
                                <select name="size" id="size" required onChange={validateField}>
                                    <option value="">Select Cylinder size</option>
                                    <option value="X2">2 Litre (handheld refillable)</option>
                                    <option value="X7">7 Litre (knee height)</option>
                                    <option value="X10">10 Litre (knee height)</option>
                                    <option value="X20">20 Litre (waist height)</option>
                                    <option value="X47">47 Litre (shoulder height)</option>
                                    <option value="X50">50 Litre (shoulder height)</option>
                                    <option value="X108">47 Litre (Propane only)</option>
                                    <option value="11X47">Pack of 11x 47 Litre cylinders</option>
                                    <option value="12X50">Pack of 12x 50 Litre cylinders</option>
                                    <option value="NR">Non-Refill Canister/Aerosol</option>
                                    <option value="NR34">Non-Refill 34L Canister</option>
                                    <option value="NR58">Non-Refill 58L Canister</option>
                                    <option value="NR110">Non-Refill 110L Canister</option>
                                </select>
                                <span className="custom-select-toggle text-secondary text-2xl">
                                    <FontAwesomeIcon icon={faChevronCircleDown} />
                                </span>
                            </div>
                            <span className="error"></span>
                        </div>
                    </FieldWrapper>

                    <FieldWrapper>
                        <label htmlFor="application">Application</label>
                        <textarea name="application" id="application" cols="30" rows="4"></textarea>
                    </FieldWrapper>

                    <div className="mb-8 md:pl-52">
                        <p><sup>*</sup>All mixtures configurations will be checked for safety and feasibility prior to manufacture. Where there are concerns, we will recommend alterations to the mixture to overcome these.</p>
                    </div>

                    <span className="global-error p-4 block text-center md:text-left md:pl-52 font-bold" aria-hidden="true"></span>

                    <div className="form-controls flex justify-center mb-8 lg:mb-16">
                        <Link 
                            to="/"
                            className="block mx-3 py-4 px-8 leading-none uppercase border border-secondary rounded-full font-bold text-secondary shadow-lg bg-white hover:bg-white-dark" 
                            id="test"
                            >Back</Link>
                        {/* <Link 
                            to="/"
                            className="hidden" aria-hidden="true"
                            type="button" id="progress"
                            >Progress</Link> */}
                        <button
                            className="block mx-3 py-4 px-8 leading-none uppercase bg-secondary border border-secondary rounded-full font-bold text-white shadow-lg hover:bg-secondary-light"
                            type="submit"
                            onClick={submitForm}>Next</button>
                    </div>
                </form>    
            </Wrapper>

            <Advert />
        </main>
    )
}